/**
 * Event Monitor Tab for Platform Admin
 * Displays real-time event monitoring for the entire platform
 */
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, AlertTriangle, CheckCircle, Clock, RefreshCw, Zap, TrendingUp, Loader2 } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from "recharts";

// Event type colors
const EVENT_COLORS = {
  order_created: '#22c55e',
  order_paid: '#3b82f6',
  order_ready: '#8b5cf6',
  order_delivered: '#06b6d4',
  order_cancelled: '#ef4444',
  payment_received: '#10b981',
  payment_failed: '#f97316',
  customer_created: '#ec4899',
  customer_returned: '#a855f7',
  cart_abandoned: '#f59e0b',
  coupon_used: '#14b8a6',
  restaurant_onboarded: '#6366f1',
  menu_updated: '#84cc16',
};

// Event type labels
const EVENT_LABELS = {
  order_created: 'Pedido Criado',
  order_paid: 'Pedido Pago',
  order_ready: 'Pedido Pronto',
  order_delivered: 'Pedido Entregue',
  order_cancelled: 'Pedido Cancelado',
  payment_received: 'Pagamento Recebido',
  payment_failed: 'Pagamento Falhou',
  customer_created: 'Cliente Criado',
  customer_returned: 'Cliente Retornou',
  cart_abandoned: 'Carrinho Abandonado',
  coupon_used: 'Cupom Usado',
  restaurant_onboarded: 'Restaurante Onboarded',
  menu_updated: 'Menu Atualizado',
};

export const EventMonitorTab = ({ adminAxios }) => {
  const [summary, setSummary] = useState(null);
  const [liveEvents, setLiveEvents] = useState([]);
  const [failedEvents, setFailedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('24');
  const [selectedEventType, setSelectedEventType] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      // Fetch summary
      const summaryRes = await adminAxios.get(`/event-monitor/summary?hours=${selectedPeriod}`);
      setSummary(summaryRes.data);
      
      // Fetch live events
      const liveUrl = selectedEventType === 'all' 
        ? `/event-monitor/live?limit=30`
        : `/event-monitor/live?limit=30&event_type=${selectedEventType}`;
      const liveRes = await adminAxios.get(liveUrl);
      setLiveEvents(liveRes.data.events || []);
      
      // Fetch failed events
      const failedRes = await adminAxios.get(`/event-monitor/failed?limit=20`);
      setFailedEvents(failedRes.data.events || []);
      
    } catch (error) {
      console.error('Error fetching event data:', error);
    } finally {
      setLoading(false);
    }
  }, [adminAxios, selectedPeriod, selectedEventType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  // Prepare chart data
  const eventTypeData = summary?.events_by_type 
    ? Object.entries(summary.events_by_type).map(([type, count]) => ({
        name: EVENT_LABELS[type] || type,
        value: count,
        color: EVENT_COLORS[type] || '#94a3b8'
      }))
    : [];

  const hourlyData = summary?.hourly_distribution?.map(item => ({
    hour: new Date(item.hour).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    events: item.count
  })) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="event-monitor-tab">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-500" />
            Monitor de Eventos
          </h2>
          <p className="text-muted-foreground text-sm">Arquitetura Event-Driven em tempo real</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-28 h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 hora</SelectItem>
              <SelectItem value="6">6 horas</SelectItem>
              <SelectItem value="24">24 horas</SelectItem>
              <SelectItem value="48">48 horas</SelectItem>
              <SelectItem value="168">7 dias</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className="gap-1 h-8"
            data-testid="auto-refresh-btn"
          >
            <RefreshCw className={`h-3 w-3 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto' : 'Manual'}
          </Button>
          
          <Button variant="outline" size="sm" onClick={fetchData} className="h-8" data-testid="refresh-btn">
            <RefreshCw className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-xs">Total de Eventos</p>
                <p className="text-2xl font-bold">{summary?.total_events || 0}</p>
              </div>
              <Zap className="h-6 w-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-xs">Tipos de Evento</p>
                <p className="text-2xl font-bold">{Object.keys(summary?.events_by_type || {}).length}</p>
              </div>
              <TrendingUp className="h-6 w-6 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-xs">Eventos Falhos</p>
                <p className="text-2xl font-bold">{summary?.failed_events || 0}</p>
              </div>
              {summary?.failed_events > 0 ? (
                <AlertTriangle className="h-6 w-6 text-red-500" />
              ) : (
                <CheckCircle className="h-6 w-6 text-green-500" />
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-xs">Período</p>
                <p className="text-2xl font-bold">{selectedPeriod}h</p>
              </div>
              <Clock className="h-6 w-6 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-6">
        {/* Events by Type */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Eventos por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            {eventTypeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={eventTypeData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" stroke="#9ca3af" fontSize={10} />
                  <YAxis type="category" dataKey="name" stroke="#9ca3af" width={110} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {eventTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[220px] text-muted-foreground">
                Nenhum evento no período
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hourly Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Distribuição por Hora</CardTitle>
          </CardHeader>
          <CardContent>
            {hourlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="hour" stroke="#9ca3af" tick={{ fontSize: 9 }} />
                  <YAxis stroke="#9ca3af" fontSize={10} />
                  <Tooltip />
                  <Area type="monotone" dataKey="events" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[220px] text-muted-foreground">
                Nenhum evento no período
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Live Events & Failed Events */}
      <div className="grid grid-cols-2 gap-6">
        {/* Live Events */}
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
              Eventos em Tempo Real
            </CardTitle>
            <Select value={selectedEventType} onValueChange={setSelectedEventType}>
              <SelectTrigger className="w-36 h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(EVENT_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="max-h-[350px] overflow-y-auto">
            {liveEvents.length > 0 ? (
              <div className="space-y-2">
                {liveEvents.map((event, index) => (
                  <div 
                    key={event.id || index} 
                    className="flex items-start gap-2 p-2 bg-muted/50 rounded-lg border"
                    data-testid={`event-${event.type}`}
                  >
                    <div 
                      className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" 
                      style={{ backgroundColor: EVENT_COLORS[event.type] || '#94a3b8' }}
                    ></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Badge 
                          variant="outline" 
                          className="text-[10px] px-1 h-4"
                          style={{ borderColor: EVENT_COLORS[event.type], color: EVENT_COLORS[event.type] }}
                        >
                          {EVENT_LABELS[event.type] || event.type}
                        </Badge>
                        <span className="text-muted-foreground text-[10px]">{formatTime(event.timestamp)}</span>
                      </div>
                      <p className="text-xs truncate">{event.data_preview}</p>
                      {event.handlers_status && Object.keys(event.handlers_status).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Object.entries(event.handlers_status).slice(0, 3).map(([handler, status]) => (
                            <span 
                              key={handler} 
                              className={`text-[9px] px-1 py-0.5 rounded ${
                                status.status === 'success' ? 'bg-green-100 text-green-700' : 
                                status.status === 'failed' ? 'bg-red-100 text-red-700' : 
                                'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {handler.replace('_handler', '').replace('_', ' ')}
                            </span>
                          ))}
                          {Object.keys(event.handlers_status).length > 3 && (
                            <span className="text-[9px] text-muted-foreground">
                              +{Object.keys(event.handlers_status).length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                <Activity className="h-10 w-10 mb-2 opacity-30" />
                <p className="text-sm">Nenhum evento recente</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Failed Events */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Eventos com Falha
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[350px] overflow-y-auto">
            {failedEvents.length > 0 ? (
              <div className="space-y-2">
                {failedEvents.map((event, index) => (
                  <div 
                    key={event.event_id || index} 
                    className="p-2 bg-red-50 rounded-lg border border-red-200"
                    data-testid={`failed-event-${index}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="destructive" className="text-[10px] h-4">
                        {EVENT_LABELS[event.event_type] || event.event_type}
                      </Badge>
                      <span className="text-muted-foreground text-[10px]">{formatDate(event.failed_at)}</span>
                    </div>
                    <p className="text-red-600 text-xs mb-0.5">Handler: {event.handler}</p>
                    <p className="text-muted-foreground text-[10px] truncate">Erro: {event.error}</p>
                    <p className="text-muted-foreground text-[10px]">Tentativas: {event.attempts}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                <CheckCircle className="h-10 w-10 mb-2 text-green-500 opacity-50" />
                <p className="text-sm">Nenhum evento com falha</p>
                <p className="text-xs text-green-600 mt-1">Sistema operando normalmente</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Event Type Legend */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Legenda de Eventos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
            {Object.entries(EVENT_LABELS).map(([key, label]) => (
              <div key={key} className="flex items-center gap-1.5">
                <div 
                  className="w-2 h-2 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: EVENT_COLORS[key] }}
                ></div>
                <span className="text-muted-foreground text-[10px] truncate">{label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
